import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  DirectionsCar,
  Person,
  CreditCard,
  Search as SearchIcon,
  CheckCircle,
  LocationOn,
  Star,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface MultiStepBookingDialogProps {
  open: boolean;
  onClose: () => void;
  packageType: 'flex' | 'premium' | null;
  step: number;
  customerTab: number;
  onCustomerTabChange: (tab: number) => void;
  existingCustomers: any[];
  selectedCustomer: any;
  onCustomerSelect: (customer: any) => void;
  loadingCustomers: boolean;
  formData: any;
  onFormChange: (field: string, value: any) => void;
  availableVehicleTypes: string[];
  onSearch: () => void;
  searchResults: any[];
  selectedVehicle: any;
  onSelectVehicle: (vehicle: any) => void;
  onProcessPayment: (paymentMethod: 'pay-now' | 'send-link') => void;
  searchingVehicles: boolean;
  processingPayment: boolean;
  calculatedCosts: any;
  calculatingCosts: boolean;
}

const TRANSMISSION_TYPES = [
  'Automatic',
  'Manual',
];

const FUEL_TYPES = [
  'Petrol',
  'Diesel',
  'Electric',
  'Hybrid',
];

export const MultiStepBookingDialog: React.FC<MultiStepBookingDialogProps> = ({
  open,
  onClose,
  packageType,
  step,
  customerTab,
  onCustomerTabChange,
  existingCustomers,
  selectedCustomer,
  onCustomerSelect,
  loadingCustomers,
  availableVehicleTypes,
  formData,
  onFormChange,
  onSearch,
  searchResults,
  selectedVehicle,
  onSelectVehicle,
  onProcessPayment,
  searchingVehicles,
  processingPayment,
  calculatedCosts,
  calculatingCosts,
}) => {
  const steps = ['Search Criteria', 'Select Vehicle', 'Payment'];
  const [paymentMethod, setPaymentMethod] = React.useState<'pay-now' | 'send-link'>('send-link');

  const renderSearchStep = () => (
    <Grid container spacing={3}>
      {/* Customer Information */}
      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Customer Information</Typography>

        <Tabs value={customerTab} onChange={(_, v) => onCustomerTabChange(v)} sx={{ mb: 2 }}>
          <Tab label="Existing Customer" />
          <Tab label="New Customer" />
        </Tabs>

        {customerTab === 0 ? (
          <Autocomplete
            options={existingCustomers}
            getOptionLabel={(option) => {
              if (!option) return '';
              return `${option.name || 'Unknown'} (${option.email || 'No email'})`;
            }}
            value={selectedCustomer}
            onChange={(_, newValue) => onCustomerSelect(newValue)}
            loading={loadingCustomers}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Customer"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCustomers ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{option.name || 'Unknown'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email || 'No email'} • {option.phone || 'No phone'}
                  </Typography>
                </Box>
              </li>
            )}
          />
        ) : (
          <Box>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.customerName}
              onChange={(e) => onFormChange('customerName', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.customerEmail}
              onChange={(e) => onFormChange('customerEmail', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Phone"
              value={formData.customerPhone}
              onChange={(e) => onFormChange('customerPhone', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Identity Verification</Typography>
            <TextField
              fullWidth
              label="NIN"
              value={formData.customerNIN}
              onChange={(e) => onFormChange('customerNIN', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Driver's License"
              value={formData.customerDriverLicense}
              onChange={(e) => onFormChange('customerDriverLicense', e.target.value)}
            />
          </Box>
        )}
      </Grid>

      {/* Booking Criteria */}
      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Booking Criteria</Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => onFormChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={(e) => onFormChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: formData.startDate }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Pickup Time"
              type="time"
              value={formData.startTime}
              onChange={(e) => onFormChange('startTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Dropoff Time"
              type="time"
              value={formData.endTime}
              onChange={(e) => onFormChange('endTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Pickup Location"
          value={formData.pickupLocation}
          onChange={(e) => onFormChange('pickupLocation', e.target.value)}
          sx={{ mt: 2, mb: 2 }}
          placeholder="e.g., Lagos, VI"
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Vehicle Type (Multi-select)</InputLabel>
          <Select
            multiple
            value={formData.vehicleTypes}
            onChange={(e) => onFormChange('vehicleTypes', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {availableVehicleTypes.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Transmission</InputLabel>
              <Select
                value={formData.transmission || ''}
                onChange={(e) => onFormChange('transmission', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {TRANSMISSION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Fuel Type</InputLabel>
              <Select
                value={formData.fuelType || ''}
                onChange={(e) => onFormChange('fuelType', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {FUEL_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {formData.vehicleTypes.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Selected: {formData.vehicleTypes.join(', ')}
            </Typography>
          </Alert>
        )}

        <TextField
          fullWidth
          label="Dropoff Location (optional)"
          value={formData.dropoffLocation}
          onChange={(e) => onFormChange('dropoffLocation', e.target.value)}
          placeholder="Same as pickup if not specified"
        />
      </Grid>
    </Grid>
  );

  const renderResultsStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {searchResults.length} vehicles found
      </Typography>

      {searchResults.length === 0 ? (
        <Alert severity="info">
          No vehicles found matching your criteria. Try adjusting your search.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {searchResults.map((vehicle) => (
            <Grid item xs={12} md={6} key={vehicle.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedVehicle?.id === vehicle.id ? '2px solid' : '1px solid',
                  borderColor: selectedVehicle?.id === vehicle.id ? 'primary.main' : 'divider',
                  '&:hover': { boxShadow: 4 },
                }}
                onClick={() => onSelectVehicle(vehicle)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                      <DirectionsCar />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">
                        {vehicle.name || `${vehicle.make} ${vehicle.model}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {vehicle.year} • {vehicle.category || 'Standard'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">{vehicle.location || 'Lagos'}</Typography>
                      </Box>
                      {vehicle.host && (
                        <Typography variant="caption" color="text.secondary">
                          Host: {vehicle.host.name || 'N/A'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h5" color="primary">
                        ₦{(vehicle.pricePerDay || vehicle.dailyRate || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="caption">per day</Typography>
                    </Box>
                    {vehicle.rating && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Star fontSize="small" sx={{ color: 'gold' }} />
                        <Typography variant="body2">{vehicle.rating}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderPaymentStep = () => {
    if (!selectedVehicle) return null;

    const days = Math.ceil(
      (new Date(formData.endDate).getTime() - new Date(formData.startDate).getTime()) /
      (1000 * 60 * 60 * 24)
    ) || 1;

    // Get customer email for validation
    const customerEmail = customerTab === 0 ? selectedCustomer?.email : formData.customerEmail;
    const hasValidEmail = customerEmail && customerEmail.includes('@');

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Booking Summary</Typography>

        {calculatingCosts && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Calculating costs...</Typography>
            </Box>
          </Alert>
        )}

        {!hasValidEmail && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Email Required:</strong> Paystack requires a valid email address to process payments.
              {customerTab === 1 && ' Please go back and enter the customer\'s email.'}
            </Typography>
          </Alert>
        )}

        {!calculatedCosts && !calculatingCosts && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Cost Calculation Failed:</strong> Unable to calculate booking costs. Please go back and try selecting the vehicle again.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                <Typography variant="h6">
                  {customerTab === 0 ? selectedCustomer?.name : formData.customerName}
                </Typography>
                <Typography variant="body2" color={hasValidEmail ? 'text.primary' : 'error'}>
                  {customerEmail || 'No email provided'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Vehicle</Typography>
                <Typography variant="h6">
                  {selectedVehicle.name || `${selectedVehicle.make} ${selectedVehicle.model}`}
                </Typography>
                <Typography variant="body2">{selectedVehicle.category || 'Standard'}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Rental Period
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Pickup"
                      secondary={`${formData.startDate} at ${formData.startTime}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Dropoff"
                      secondary={`${formData.endDate} at ${formData.endTime}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Number of Days"
                      secondary={`${days} day${days !== 1 ? 's' : ''}`}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>💰 Payment Breakdown</Typography>

                {calculatedCosts ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Customer Pays:</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Rental Cost ({days} day{days !== 1 ? 's' : ''})</Typography>
                      <Typography variant="body2" fontWeight="bold">₦{calculatedCosts.rentalCost?.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Booking Fee ({calculatedCosts.bookingFeeRate}%)</Typography>
                      <Typography variant="body2">₦{calculatedCosts.bookingFee?.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">VAT ({calculatedCosts.vatPercentage}%)</Typography>
                      <Typography variant="body2">₦{calculatedCosts.vat?.toLocaleString()}</Typography>
                    </Box>
                    {calculatedCosts.securityDeposit?.amount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="warning.main">Security Deposit (Refundable)</Typography>
                        <Typography variant="body2" color="warning.main">₦{calculatedCosts.securityDeposit.amount?.toLocaleString()}</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, bgcolor: 'primary.lighter', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="h6">Total Amount</Typography>
                      <Typography variant="h6" color="primary">₦{calculatedCosts.totalCost?.toLocaleString()}</Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="caption">
                        Platform service fee ({calculatedCosts.serviceFeeRate}%: ₦{calculatedCosts.serviceFee?.toLocaleString()}) is deducted from host payout
                      </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'success.lighter', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="body2" color="success.dark">Host Receives</Typography>
                      <Typography variant="body2" color="success.dark" fontWeight="bold">
                        ₦{calculatedCosts.hostPayout?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Cost breakdown not yet calculated
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Payment Method
                </Typography>
                <RadioGroup
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as 'pay-now' | 'send-link')}
                >
                  <FormControlLabel
                    value="send-link"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Send Payment Link
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Email payment link to customer - they pay later
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: 2,
                      border: paymentMethod === 'send-link' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      borderRadius: 2,
                      mb: 1
                    }}
                  />
                  <FormControlLabel
                    value="pay-now"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Process Payment Now
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Open Paystack popup for immediate payment
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: 2,
                      border: paymentMethod === 'pay-now' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      borderRadius: 2
                    }}
                  />
                </RadioGroup>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h5">
            Create New Car Rental - {packageType === 'flex' ? 'Flex Package' : 'Premium Package'}
          </Typography>
          <Stepper activeStep={step - 1} sx={{ mt: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {step === 1 && renderSearchStep()}
          {step === 2 && renderResultsStep()}
          {step === 3 && renderPaymentStep()}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {step > 1 && (
          <Button onClick={() => onFormChange('step', step - 1)}>
            Back
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="contained"
            onClick={onSearch}
            disabled={
              searchingVehicles ||
              !formData.pickupLocation ||
              (customerTab === 0 && !selectedCustomer) ||
              (customerTab === 1 && !formData.customerEmail)
            }
            startIcon={searchingVehicles ? <CircularProgress size={20} /> : <SearchIcon />}
          >
            {searchingVehicles ? 'Searching...' : 'Search Vehicles'}
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            disabled={!selectedVehicle}
            onClick={() => onFormChange('step', 3)}
          >
            Continue to Payment
          </Button>
        )}
        {step === 3 && (() => {
          const customerEmail = customerTab === 0 ? selectedCustomer?.email : formData.customerEmail;
          const hasValidEmail = customerEmail && customerEmail.includes('@');
          const hasCosts = calculatedCosts && !calculatingCosts;
          const isDisabled = processingPayment || !hasValidEmail || !hasCosts;
          const buttonText = paymentMethod === 'send-link'
            ? (processingPayment ? 'Sending Link...' : 'Create Booking & Send Link')
            : (processingPayment ? 'Processing...' : 'Process Payment Now');

          let tooltipText = paymentMethod === 'send-link'
            ? 'Create booking and email payment link to customer'
            : 'Open Paystack payment popup';

          if (!hasValidEmail) {
            tooltipText = 'Valid email required';
          } else if (!hasCosts) {
            tooltipText = 'Calculating costs...';
          }

          return (
            <Tooltip title={tooltipText}>
              <span>
                <Button
                  variant="contained"
                  onClick={() => onProcessPayment(paymentMethod)}
                  disabled={isDisabled}
                  startIcon={processingPayment ? <CircularProgress size={20} /> : <CreditCard />}
                  color="success"
                >
                  {buttonText}
                </Button>
              </span>
            </Tooltip>
          );
        })()}
      </DialogActions>
    </Dialog>
  );
};
